import { isMicroappsEnabled } from './utils';

declare const microapps: any;

export const APP_STORE_URL =
  'itms-apps://apps.apple.com/in/app/freo-upi-insurance-loans/id1588894115';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.moneytap.bnpl.app';

function getUserAgent(): string {
  return navigator.userAgent || navigator.vendor || '';
}
function isIOSDevice(): boolean {
  return /iPad|iPhone|iPod/i.test(getUserAgent());
}
function isAndroidDevice(): boolean {
  return /android/i.test(getUserAgent());
}

// iOS -> App Store; anything else (Android, desktop, unrecognized UA) -> Play Store.
export function getStoreUrl(): string {
  return isIOSDevice() ? APP_STORE_URL : PLAY_STORE_URL;
}

export type StoreNavigationMode = 'new-tab' | 'same-tab' | 'replace';

export interface OpenStoreLinkOptions {
  url?: string; // overrides the computed store URL (e.g. backend-supplied redirectUrl)
  navigation?: StoreNavigationMode; // browser fallback style when not bridge-routed; default 'new-tab'
}

// Opens the platform-appropriate store link (or options.url override).
// Routes through the native microapps bridge when running inside a WebView,
// otherwise falls back to plain browser navigation per `navigation`.
export function openStoreLink(options: OpenStoreLinkOptions = {}): void {
  const url = options.url || getStoreUrl();
  if (isMicroappsEnabled()) {
    try {
      microapps.openUrl({ url });
    } catch {
      console.log('Unable to open url');
    }
    return;
  }
  const navigation = options.navigation ?? 'new-tab';
  if (navigation === 'replace') {
    window.location.href = url;
  } else {
    window.open(url, navigation === 'same-tab' ? '_self' : '_blank');
  }
}

// For deep-link fallback routes (router guard / NotFound.vue): iOS/Android -> store
// (bridge-aware); desktop/unrecognized UA -> caller-supplied fallback (each caller's
// "go home" target differs slightly, so that part stays caller-owned).
export function redirectToStoreOrFallback(onFallback: () => void): void {
  if (isIOSDevice() || isAndroidDevice()) {
    openStoreLink({ navigation: 'replace' });
  } else {
    onFallback();
  }
}

// The two collections repayment links, marked with ?target=web:
//   /app/one_app/nav/repayment/pay?target=web
//   /app/one_app/nav/repayment/borrow?target=web
//
// These reach the web repayments page instead of the store. The customer on the
// other end of a collections link already owes money, and sending them to a
// 65MB install to collect it is a guaranteed drop-off - worse, with AppsFlyer
// gone they would land on the app's home screen rather than on repayments
// anyway, so the install does not even get them there.
//
// The marker is a query param rather than a path segment so the path stays
// exactly as it is today. App Link matching - Android's manifest intent filters
// and the paths claimed in apple-app-site-association - looks at the path only,
// so an installed app still opens on a path it already handles and ignores a
// param it does not know. Nothing has to change in the app for these to work.
//
// Name follows the target=mt convention already used for picking which store
// listing a link opens, so target=web reads as one more destination.
export const REPAYMENT_DEEP_LINK_PATHS = [
  '/app/one_app/nav/repayment/pay',
  '/app/one_app/nav/repayment/borrow',
];

export const WEB_FALLBACK_PARAM = 'target';
export const WEB_FALLBACK_PARAM_VALUE = 'web';

// Vue Router hands back an array when a param is repeated (?target=web&target=x),
// so the first value is what counts - matching how the browser and the backend
// would read it.
export function wantsWebFallback(query: any): boolean {
  const raw = query ? query[WEB_FALLBACK_PARAM] : undefined;
  const value = Array.isArray(raw) ? raw[0] : raw;
  return value === WEB_FALLBACK_PARAM_VALUE;
}
